import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const stappen = [
  { number: '01', icon: 'ri-send-plane-line', title: 'Stuur je product of idee', description: 'Deel je productomschrijving, afmetingen, kwaliteitseisen en gewenste hoeveelheid. Per mail, WhatsApp of formulier.', duur: 'Vandaag nog' },
  { number: '02', icon: 'ri-search-eye-line', title: 'Wij doen een prijscheck in China', description: 'Ons netwerk van fabrieken wordt ingezet om de beste prijs-kwaliteitsverhouding te vinden.', duur: '1–2 werkdagen' },
  { number: '03', icon: 'ri-file-list-3-line', title: 'Je ontvangt een voorstel', description: 'Een helder voorstel met prijs, levertijd, MOQ en all-in kosten. Geen verrassingen.', duur: 'Binnen 24 uur' },
  { number: '04', icon: 'ri-settings-3-line', title: 'Productie + begeleiding', description: 'Wij begeleiden de productie, controleren kwaliteit en zorgen voor tijdige afronding.', duur: 'Korte levertijden!' },
  { number: '05', icon: 'ri-truck-line', title: 'Jij bespaart op inkoop', description: 'Levering rechtstreeks naar jou, meestal DDP, dus inclusief transport, invoerrechten en BTW.', duur: 'All-in prijs' },
];

export default function HowItWorks() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="proces" ref={ref} className="py-12 md:py-20 bg-[#F7F8FA] overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-6 lg:px-10">
        <div className="text-center mb-10 md:mb-14">
          <motion.span initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }} className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-600 mb-6">
            <i className="ri-route-line" />
            Hoe het werkt
          </motion.span>
          <motion.h2 initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.1 }} className="font-heading font-800 text-[#0A1628] text-3xl md:text-4xl mb-3">
            Van idee naar levering
            <br />
            <span className="text-[#3B9EE8]">in 5 stappen</span>
          </motion.h2>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.2 }} className="text-[#0A1628]/55 text-lg max-w-2xl mx-auto">
            Simpel, transparant en zonder gedoe. DUC regelt alles van begin tot eind.
          </motion.p>
        </div>

        <div className="relative">
          <div className="hidden lg:block absolute top-16 left-[calc(10%+32px)] right-[calc(10%+32px)] h-0.5 bg-[#3B9EE8]/15" />
          <motion.div
            initial={{ scaleX: 0 }}
            animate={inView ? { scaleX: 1 } : {}}
            transition={{ duration: 1.2, delay: 0.4, ease: 'easeOut' }}
            style={{ transformOrigin: 'left' }}
            className="hidden lg:block absolute top-16 left-[calc(10%+32px)] right-[calc(10%+32px)] h-0.5 bg-[#3B9EE8]"
          />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {stappen.map((stap, i) => (
              <motion.div key={stap.number} initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }} className="group relative bg-white rounded-xl p-5 cursor-default border border-[#0A1628]/5 hover:border-[#3B9EE8]/30 transition-all duration-300">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#0A1628] border-2 border-[#3B9EE8] mb-4 relative z-10">
                  <span className="font-heading font-700 text-[#3B9EE8] text-xs">{stap.number}</span>
                </div>
                <div className="w-8 h-8 flex items-center justify-center mb-3">
                  <i className={`${stap.icon} text-[#3B9EE8] text-xl`} />
                </div>
                <h3 className="font-heading font-700 text-[#0A1628] text-xs md:text-sm mb-2 leading-snug">{stap.title}</h3>
                <p className="text-[#0A1628]/55 text-xs leading-relaxed mb-4 hidden md:block">{stap.description}</p>
                <div className="inline-flex items-center gap-1 bg-[#3B9EE8]/10 rounded-full px-2.5 py-1">
                  <i className="ri-time-line text-[#3B9EE8] text-xs" />
                  <span className="text-[#3B9EE8] text-xs font-600">{stap.duur}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 1 }} className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white border border-[#0A1628]/6 rounded-xl p-6 mt-10">
          <div>
            <p className="font-700 text-[#0A1628] text-base">Wil je het volledige proces zien?</p>
            <p className="text-[#0A1628]/50 text-sm mt-0.5">Bekijk elk stap in detail — van idee tot levering aan jouw deur.</p>
          </div>
          <a href="/hoe-het-werkt" className="inline-flex items-center gap-2 bg-[#0A1628] hover:bg-[#0D2147] text-white px-6 py-3 rounded-lg text-sm font-700 transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0">
            Bekijk volledig proces
            <i className="ri-arrow-right-line" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
